// JSON-LD (schema.org) node builders. Pure functions — every value is passed in by the
// caller so the markup can only ever contain data that is actually on the page.
//
// Node ids follow one convention so nodes can reference each other across the two script
// blocks the site emits (site-level graph in the default layout, page-level graph in the
// product listing layout):
//   {siteUrl}#website · {siteUrl}#organization · {siteUrl}#logo
//   {canonicalUrl}#webpage · #breadcrumb · #itemlist · #faq

import { absoluteUrl, productPathFromSeoUrls } from './seo';

export interface JsonLdNode {
    '@type': string | string[];
    '@id'?: string;
    [key: string]: unknown;
}

export interface BreadcrumbInput {
    name?: string;
    url?: string;
}

export interface PostalAddressInput {
    street?: string | null;
    postalCode?: string | null;
    city?: string | null;
    country?: string;
}

/** Recursively drop null/undefined/empty values so no empty placeholder ends up in the output. */
export function prune<T>(value: T): T | undefined {
    if (Array.isArray(value)) {
        const items = value.map(prune).filter(item => item !== undefined);
        return (items.length ? items : undefined) as T | undefined;
    }
    if (value && typeof value === 'object') {
        const entries = Object.entries(value as Record<string, unknown>)
            .map(([key, val]) => [key, prune(val)] as const)
            .filter(([, val]) => val !== undefined);
        return (entries.length ? Object.fromEntries(entries) : undefined) as T | undefined;
    }
    if (typeof value === 'string' && value.trim() === '') return undefined;
    return value ?? undefined;
}

/**
 * Serialize a graph for inlining in `<script type="application/ld+json">`. `<`, `>` and `&`
 * are escaped so page content can never break out of the script tag.
 */
export function jsonLdGraph(nodes: Array<JsonLdNode | undefined>): string {
    const graph = nodes.map(node => prune(node)).filter(Boolean);
    return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/&/g, '\\u0026');
}

export function websiteNode(input: {
    siteUrl: string;
    name: string;
    searchUrlTemplate?: string;
    inLanguage?: string;
}): JsonLdNode {
    return {
        '@type': 'WebSite',
        '@id': `${input.siteUrl}#website`,
        url: input.siteUrl,
        name: input.name,
        inLanguage: input.inLanguage ?? 'nl-NL',
        publisher: { '@id': `${input.siteUrl}#organization` },
        potentialAction: input.searchUrlTemplate
            ? {
                  '@type': 'SearchAction',
                  target: {
                      '@type': 'EntryPoint',
                      urlTemplate: input.searchUrlTemplate,
                  },
                  'query-input': 'required name=search_term_string',
              }
            : undefined,
    };
}

export function organizationNode(input: {
    siteUrl: string;
    name: string;
    logoUrl?: string;
    telephone?: string;
    email?: string;
    address?: PostalAddressInput;
    sameAs?: Array<string | null | undefined>;
    openingHoursSpecification?: JsonLdNode[];
}): JsonLdNode {
    const logo = input.logoUrl
        ? {
              '@type': 'ImageObject',
              '@id': `${input.siteUrl}#logo`,
              url: input.logoUrl,
              contentUrl: input.logoUrl,
          }
        : undefined;

    const address = input.address
        ? {
              '@type': 'PostalAddress',
              streetAddress: input.address.street,
              postalCode: input.address.postalCode,
              addressLocality: input.address.city,
              addressCountry: input.address.country ?? 'NL',
          }
        : undefined;

    return {
        '@type': ['Organization', 'ClothingStore'],
        '@id': `${input.siteUrl}#organization`,
        name: input.name,
        url: input.siteUrl,
        logo,
        image: logo ? { '@id': `${input.siteUrl}#logo` } : undefined,
        telephone: input.telephone,
        email: input.email,
        address,
        contactPoint: input.telephone
            ? {
                  '@type': 'ContactPoint',
                  telephone: input.telephone,
                  contactType: 'customer service',
                  areaServed: 'NL',
                  availableLanguage: ['nl'],
              }
            : undefined,
        openingHoursSpecification: input.openingHoursSpecification,
        sameAs: (input.sameAs ?? []).filter(Boolean) as string[],
    };
}

export function collectionPageNode(input: {
    siteUrl: string;
    canonicalUrl: string;
    name?: string;
    headline?: string;
    description?: string;
    about?: JsonLdNode | JsonLdNode[];
    hasItemList?: boolean;
    inLanguage?: string;
}): JsonLdNode {
    return {
        '@type': 'CollectionPage',
        '@id': `${input.canonicalUrl}#webpage`,
        url: input.canonicalUrl,
        name: input.name,
        headline: input.headline,
        description: input.description,
        inLanguage: input.inLanguage ?? 'nl-NL',
        isFamilyFriendly: true,
        isPartOf: { '@id': `${input.siteUrl}#website` },
        publisher: { '@id': `${input.siteUrl}#organization` },
        copyrightHolder: { '@id': `${input.siteUrl}#organization` },
        about: input.about,
        breadcrumb: { '@id': `${input.canonicalUrl}#breadcrumb` },
        mainEntity: input.hasItemList ? { '@id': `${input.canonicalUrl}#itemlist` } : undefined,
    };
}

/**
 * BreadcrumbList for the visible breadcrumb trail. Shopware returns an empty `url` for the
 * current page (it is not a link), so that item falls back to the canonical URL.
 */
export function breadcrumbListNode(input: {
    baseUrl: string;
    canonicalUrl: string;
    items: BreadcrumbInput[];
}): JsonLdNode | undefined {
    const itemListElement = (input.items ?? [])
        .filter(crumb => !!crumb?.name)
        .map((crumb, index, all) => {
            const isLast = index === all.length - 1;
            const url = crumb.url ? absoluteUrl(input.baseUrl, crumb.url) : isLast ? input.canonicalUrl : '';
            return {
                '@type': 'ListItem',
                position: index + 1,
                name: crumb.name,
                item: url,
            };
        })
        .filter(item => !!item.item);

    if (itemListElement.length < 2) return undefined;

    return {
        '@type': 'BreadcrumbList',
        '@id': `${input.canonicalUrl}#breadcrumb`,
        itemListElement,
    };
}

/**
 * ItemList of the products visible in the listing. Conservative by design: position + URL
 * only, so nothing can contradict the price/stock the visitor sees.
 */
export function itemListNode(input: {
    baseUrl: string;
    canonicalUrl: string;
    name?: string;
    products: any[];
    startPosition?: number;
}): JsonLdNode | undefined {
    const start = input.startPosition && input.startPosition > 0 ? input.startPosition : 1;
    const itemListElement = (input.products ?? [])
        .map(product => productPathFromSeoUrls(product))
        .filter((path): path is string => !!path)
        .map((path, index) => ({
            '@type': 'ListItem',
            position: start + index,
            url: absoluteUrl(input.baseUrl, path),
        }));

    if (!itemListElement.length) return undefined;

    return {
        '@type': 'ItemList',
        '@id': `${input.canonicalUrl}#itemlist`,
        url: input.canonicalUrl,
        name: input.name,
        numberOfItems: itemListElement.length,
        itemListElement,
    };
}

/**
 * Product with its Offer, for the product detail page. Price and availability are passed in by
 * the caller from exactly the values the page renders, so the markup cannot drift from what the
 * visitor sees.
 */
export function productNode(input: {
    siteUrl: string;
    canonicalUrl: string;
    name: string;
    description?: string;
    sku?: string;
    gtin13?: string;
    mpn?: string;
    brandName?: string;
    color?: string;
    category?: string;
    images?: Array<string | undefined>;
    price?: number;
    priceCurrency?: string;
    inStock?: boolean;
}): JsonLdNode | undefined {
    if (!input.name) return undefined;

    const hasPrice = typeof input.price === 'number' && Number.isFinite(input.price);

    return {
        '@type': 'Product',
        '@id': `${input.canonicalUrl}#product`,
        url: input.canonicalUrl,
        name: input.name,
        description: input.description,
        sku: input.sku,
        gtin13: input.gtin13,
        mpn: input.mpn,
        color: input.color,
        category: input.category,
        brand: input.brandName ? { '@type': 'Brand', name: input.brandName } : undefined,
        image: (input.images ?? []).filter(Boolean) as string[],
        offers: hasPrice
            ? {
                  '@type': 'Offer',
                  '@id': `${input.canonicalUrl}#offer`,
                  url: input.canonicalUrl,
                  price: input.price!.toFixed(2),
                  priceCurrency: input.priceCurrency ?? 'EUR',
                  availability: input.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
                  itemCondition: 'https://schema.org/NewCondition',
                  seller: { '@id': `${input.siteUrl}#organization` },
              }
            : undefined,
    };
}

export function faqPageNode(input: {
    canonicalUrl: string;
    questions: Array<{ question?: string; answer?: string }>;
}): JsonLdNode | undefined {
    const mainEntity = (input.questions ?? [])
        .filter(entry => !!entry?.question?.trim() && !!entry?.answer?.trim())
        .map(entry => ({
            '@type': 'Question',
            name: entry.question!.trim(),
            acceptedAnswer: {
                '@type': 'Answer',
                text: entry.answer!.trim(),
            },
        }));

    if (!mainEntity.length) return undefined;

    return {
        '@type': 'FAQPage',
        '@id': `${input.canonicalUrl}#faq`,
        mainEntity,
    };
}

/** `about` for the three listing page types. */
export function aboutNodes(input: {
    pageType: 'category' | 'brand' | 'brand_category';
    categoryName?: string;
    brandName?: string;
    brandUrl?: string;
}): JsonLdNode | JsonLdNode[] | undefined {
    const brand: JsonLdNode | undefined = input.brandName
        ? { '@type': 'Brand', name: input.brandName, url: input.brandUrl }
        : undefined;
    const category: JsonLdNode | undefined = input.categoryName
        ? { '@type': 'Thing', name: input.categoryName }
        : undefined;

    if (input.pageType === 'category') return category;
    if (input.pageType === 'brand') return brand;
    return [brand, category].filter(Boolean) as JsonLdNode[];
}
